import React, { useEffect } from "react";
import CalendarAssorts from "../Assorts";
import useCalendarSale from "../hooks/useCalendarSale";
import { Exercise } from "../../../models/Exercise";
import { useTranslation } from "react-i18next";
import useAssortLimits from "../../Universal/hooks/useAssortLimits";
import getId from "../../../../utils/getId";
import FlowPanel from "./shared/FlowPanel";

interface IProps {
  exercise: Exercise;
  refreshCapacityAfterAddTicket: () => void;
  joinToExerciseByPass: (exerciseIds: string, assignType: boolean, passId: number) => void;
  alertCalendar: string;
  dateLabel: string;
  /** Powrót na początek ścieżki (po „Kup kolejne bilety"). */
  onBuyMore?: () => void;
}

/**
 * Wariant 2-krokowy: ta sama ścieżka co przy 3 krokach, tylko bez kolumny z godzinami.
 */
const CalendarOneExercise: React.FC<IProps> = ({
  exercise,
  refreshCapacityAfterAddTicket,
  joinToExerciseByPass,
  alertCalendar,
  dateLabel,
  onBuyMore,
}: IProps) => {
  const { t } = useTranslation();
  const { getTicketsByExercise, tickets, ticketAccess, ticketLimit, getDetailsAboutExercise, exerciseDetails } =
    useCalendarSale();
  const { limits, isFallback, isLoading: isLimitsLoading, getLimitForAssort, fetchLimits } = useAssortLimits();

  useEffect(() => {
    getTicketsByExercise(exercise.exerciseGroup, exercise);
    getDetailsAboutExercise(exercise.objectId);
    fetchLimits(`${exercise.objectDb},${exercise.objectId}`);
    //eslint-disable-next-line
  }, []);

  // Oblicz globalLimit na podstawie limitów asortymentowych (analogicznie jak w singleConcert)
  const availableCapacity = exercise.capacity - exercise.reserved;
  const visibleAssortIds = new Set(tickets.map((ticket) => getId(ticket)));

  const hardLimited = limits.filter((limit) => {
    const limitAssortId = getId(limit.assortIds);
    return visibleAssortIds.has(limitAssortId) && limit.maxQuantity !== null && limit.maxQuantity !== 0;
  });

  const limitedRemainingTotal = hardLimited.reduce((sum, limit) => {
    if (limit.remainingQuantity === null || limit.remainingQuantity === undefined) return sum;
    return sum + limit.remainingQuantity;
  }, 0);

  const hasHardLimits = hardLimited.length > 0;
  const shouldFallbackToCapacity = !isFallback && hasHardLimits && limitedRemainingTotal > availableCapacity;

  const globalLimit = isFallback
    ? availableCapacity
    : !hasHardLimits || shouldFallbackToCapacity
      ? availableCapacity
      : Math.min(availableCapacity, limitedRemainingTotal);

  const limitsProps = {
    getLimitForAssort,
    isFallback,
    isLimitsLoading,
    globalLimitOverride: globalLimit,
    exerciseGroupCompositeId: getId(exercise.exerciseGroup),
  };

  return (
    <FlowPanel step={2} title={t("wybierz_rodzaj_i_ilosc_biletow")} className="flow-col--tickets">
      <CalendarAssorts
        alertCalendar={alertCalendar}
        exerciseDetails={exerciseDetails}
        joinToExerciseByPass={joinToExerciseByPass}
        tickets={tickets}
        ticketAccess={ticketAccess}
        ticketLimit={ticketLimit}
        refreshCapacityAfterAddTicket={refreshCapacityAfterAddTicket}
        summaryContext={{ date: dateLabel }}
        onBuyMore={onBuyMore}
        {...limitsProps}
      />
    </FlowPanel>
  );
};

export default CalendarOneExercise;
